import { computed, onMounted, ref, watch, type MaybeRef, toValue } from 'vue'
import type {
  IntrospectTargetPropsDetailLevel,
  IntrospectTargetResponse,
  PromptTarget
} from '../model/types'
import { getExtractionTargetFromPromptTarget } from '../model/types'
import { useAiApiCalls } from '../model/api'
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'

export function useIntrospect(options: {
  target: MaybeRef<PromptTarget>
  fetchOnMount?: boolean
}) {
  const { introspectTargetRequest } = useAiApiCalls()

  const md = new MarkdownIt({ breaks: true })
  md.renderer.rules.code_inline = (_tokens, idx) => {
    const content = _tokens[idx]!.content
    return `<span>${md.utils.escapeHtml(content)}</span>`
  }

  const introspectResponse = ref<IntrospectTargetResponse | undefined>(
    undefined
  )
  const detailLevel = ref<IntrospectTargetPropsDetailLevel>('simple')

  async function fetchIntrospect() {
    introspectResponse.value = await introspectTargetRequest({
      pathParams: {
        target: getExtractionTargetFromPromptTarget(toValue(options.target))
      },
      queryParams: {
        props_format: 'markdown-list',
        props_detail_level: detailLevel.value
      }
    })
  }

  if (options.fetchOnMount !== false) {
    onMounted(fetchIntrospect)
  }
  watch(detailLevel, fetchIntrospect)

  const renderedProperties = computed(() =>
    DOMPurify.sanitize(md.render(introspectResponse.value?.properties ?? ''))
  )
  const renderedBaseInstructions = computed(() =>
    DOMPurify.sanitize(
      md.render(introspectResponse.value?.baseInstructions ?? '')
    )
  )
  const extractionAiModel = computed(
    () => introspectResponse.value?.extractionAiModel
  )

  return {
    detailLevel,
    renderedProperties,
    renderedBaseInstructions,
    extractionAiModel,
    refetch: fetchIntrospect
  }
}
